import { get } from 'svelte/store';

import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';

import { getDeviceIcon, getDeviceImage } from 'src/helpers/device_state_view_helpers';
import { devices } from 'src/stores';

import type { NodeData } from '../types/flow-types';
export {
	formatFlowCategoryTitle,
	resolveEnergyConditionSummary,
	resolveFlowConfiguredSummary,
	resolveFlowNodeTypeLabel,
	resolveManualConditionSummary,
	resolveMathConditionSummary,
	resolveScheduleConditionSummary,
	resolveSetVariableSummary,
	resolveSunConditionSummary,
	resolveTimeConditionSummary,
	resolveVariableConditionSummary,
	resolveWeatherConditionSummary,
} from './flowNodeCardText';
import { resolveDeviceInStore } from './resolveDeviceInStore';

export type FlowCardSemanticVariant = 'trigger' | 'condition' | 'action' | 'warning';

export type FlowNodeAvatarConfig = {
	image?: string;
	icon?: string;
	backgroundColor?: AvatarColorVariantTypes;
	noBackground?: boolean;
	notification?: { type: 'success' | 'warning' | 'danger' };
};

const AVATAR_LIGHTING: Record<FlowCardSemanticVariant, AvatarColorVariantTypes> = {
	trigger: 'color-base',
	condition: 'color-base',
	action: 'color-base',
	warning: 'color-base',
};

/** Accent used by AdvancedCardV2 surface glow — aligned with each node role. */
const SURFACE_GLOW_ACCENT: Record<FlowCardSemanticVariant, string> = {
	trigger: 'var(--color-cyan)',
	condition: 'var(--color-lightblue)',
	action: 'var(--color-green)',
	warning: 'var(--color-orange)',
};

/** Strong enough to communicate node purpose while remaining clipped to the card. */
const FLOW_CARD_LIGHTING_ACTIVE = {
	interiorStrength: 0.78,
	edgeStrength: 0.72,
	interiorSize: 180,
	edgeSize: 160,
} as const;

const FLOW_CARD_LIGHTING_INACTIVE = {
	interiorStrength: 0,
	edgeStrength: 0,
	interiorSize: 0,
	edgeSize: 0,
} as const;

export type FlowCardSurfaceGlowConfig = {
	accentColor: string;
	hotspotX?: string;
	hotspotY?: string;
	active?: {
		interiorStrength?: number;
		edgeStrength?: number;
		interiorSize?: number;
		edgeSize?: number;
	};
	inactive?: {
		interiorStrength?: number;
		edgeStrength?: number;
		interiorSize?: number;
		edgeSize?: number;
	};
};

export function resolveFlowCardSurfaceGlow(variant: FlowCardSemanticVariant): FlowCardSurfaceGlowConfig {
	return {
		accentColor: SURFACE_GLOW_ACCENT[variant],
		hotspotX: '0%',
		hotspotY: '0%',
		active: { ...FLOW_CARD_LIGHTING_ACTIVE },
		inactive: { ...FLOW_CARD_LIGHTING_INACTIVE },
	};
}

export function semanticVariantToAvatarBackground(
	variant: FlowCardSemanticVariant,
): AvatarColorVariantTypes {
	return AVATAR_LIGHTING[variant];
}

export function normalizeFlowFaIcon(icon: string | undefined): string {
	if (!icon || !String(icon).trim()) return 'fa-solid fa-diagram-project';
	const s = String(icon).trim();
	if (/\bfa[srbld]?\b/.test(s) || s.includes('fa-solid') || s.includes('fa-regular')) {
		return s;
	}
	return `fa-solid ${s}`;
}

export function resolveFlowNodeDeviceFromData(
	data: NodeData,
	devicesMap: Record<string, unknown> = get(devices),
) {
	const dc = data._device_condition as Record<string, unknown> | undefined;
	const cap = data.sceneCapability as Record<string, unknown> | undefined;
	const deviceRef = data.device as unknown as Record<string, unknown> | undefined;
	const deviceId =
		deviceRef?.id ??
		(dc?.device as { id?: string } | undefined)?.id ??
		cap?.deviceId ??
		deviceRef?.id;
	if (!deviceId) return undefined;

	const channel =
		parseInt(
			String(
				(dc?.device as { channel?: string | number } | undefined)?.channel ??
					cap?.channel ??
					deviceRef?.channel ??
					'0',
			),
			10,
		) || 0;

	return resolveDeviceInStore(devicesMap as Record<string, any>, String(deviceId), channel);
}

export function resolveFlowNodeAvatar(
	data: NodeData,
	iconClass: string,
	semanticVariant: FlowCardSemanticVariant,
	hasError = false,
	devicesMap: Record<string, unknown> = get(devices),
): FlowNodeAvatarConfig {
	const device = resolveFlowNodeDeviceFromData(data, devicesMap);
	const lighting = semanticVariantToAvatarBackground(semanticVariant);
	const notification = hasError
		? ({ type: 'danger' as const } satisfies FlowNodeAvatarConfig['notification'])
		: undefined;

	if (device) {
		const image = getDeviceImage(device);
		if (image) {
			return {
				image,
				backgroundColor:
					(device.backgroundColor as AvatarColorVariantTypes | undefined) || 'color-base',
				notification,
			};
		}

		const icon = getDeviceIcon(device) || iconClass;
		return {
			icon: normalizeFlowFaIcon(icon),
			backgroundColor: (device.backgroundColor as AvatarColorVariantTypes | undefined) || lighting,
			notification,
		};
	}

	return {
		icon: iconClass,
		backgroundColor: lighting,
		notification,
	};
}

export function actionSubtitleType(action: string): 'success' | 'danger' | 'primary' | 'default' {
	const normalized = action.trim().toLowerCase();
	if (normalized.startsWith('turn on') || normalized === 'on') return 'success';
	if (normalized.startsWith('turn off') || normalized === 'off') return 'danger';
	if (normalized.startsWith('toggle')) return 'primary';
	return 'default';
}
